import { Alert, Badge, Button, Group, Modal, Stack, Text, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { rawApi } from '../api/client';
import { listKey } from '../api/queryKeys';
import { ENUMS } from '../lib/enums';
import { formatDateTime, formatRelative } from '../lib/format';
import { notifyDone, notifyError } from '../lib/notify';
import { CrudSection } from './CrudSection';

/**
 * The manager's queue of things a register did that the office would not carry out.
 *
 * A terminal that loses its internet keeps trading — that is the whole point of it — and sends the
 * night up when it gets a signal. Most of it goes on. Some of it does not: a comp approved by
 * somebody who no longer has the permission, a payment against a bill that never arrived, a gesture
 * whose timestamp is too old to post to the day it happened on. The server records every one of
 * those with a reason and never applies it. Until now that record had no reader — the endpoint
 * existed, the tests asserted it, and nothing in this portal called it, so a comp refused at two in
 * the morning was a row in a table nobody could open.
 *
 * ## Why this lives on the device screen and not on one of its own
 *
 * Because the question it answers is already being asked there. "Registers & devices" tells the owner
 * that a terminal is holding sales the office has not received — and then offers nothing that says
 * WHAT. This is that sentence expanded: the same table (`mutation_log`) feeds the count on the
 * device row and the list below it. A page of its own would be a page nobody opens except on the
 * day they already suspect something, which is exactly the failure the CAI banner exists to avoid.
 *
 * It is also the screen where the consequence lives. Retiring a register that still holds unsent trade
 * is refused, and the sentence in that warning — "it is still holding sales the office has never
 * received" — is about these rows. Deciding whether it is safe to wipe a terminal and reading what
 * that terminal sent belong in front of the same pair of eyes.
 *
 * `RejectedActionsAlert` puts the count on the dashboard, for the same reason `RangeWarnings` is
 * there: nobody opens the device screen on an ordinary Tuesday.
 *
 * ## Why the only action is "I have dealt with this"
 *
 * Three were possible and two are wrong.
 *
 *   **Carry it out anyway** — a "retry" or "approve" button — is the one that looks most useful and
 *   is the most damaging. Every refusal on this list is the capability catalog, the bill's state
 *   machine or the invoice range saying no. A button here that overrode any of them would put a
 *   bypass beside all three, reachable from a browser by anybody with an office account, for
 *   precisely the gestures those rules exist to bound — comps, voids after the food has gone out,
 *   payments. And it would land the action in the office, under whoever clicked it, hours after the
 *   fact. If a comp ought to stand it is comped again AT THE REGISTER, by somebody who holds the grant,
 *   against the tab it belongs to, through the door every other comp goes through — which leaves a
 *   trail. A button here would not.
 *
 *   **Dismiss** — delete it, hide it — destroys the only complete record of what a terminal
 *   attempted. That record is the evidence in a dispute about a guest's bill, and it is the reason
 *   the row is kept verbatim in the first place. Nothing on this screen may be the thing that makes
 *   a night unreconstructable.
 *
 *   **Acknowledge** is what is left, and it is honest about itself: it takes the row off the
 *   working list, records who looked and when, and writes an audit event carrying the reason and
 *   whatever note was left. It changes nothing about the action. The wording on the button and in
 *   the dialog says so, because "Mark as dealt with" over a comp that was never re-rung is the one
 *   way this screen could do harm.
 *
 * A row that is still WAITING gets no button at all. Those may yet apply on their own — they are
 * queued behind something that has not landed — and taking one off the list would hide a message
 * that is still in flight.
 */

export const REJECTED_MUTATIONS_PATH = '/admin/v1/operations/rejected-mutations';

/** The working list: what nobody has looked at yet. Shared by the banner and the table below it. */
const UNREVIEWED = { unreviewed: 'true' } as const;

export interface RejectedAction {
  id: string;
  type: string;
  status: 'pending' | 'accepted' | 'rejected';
  reason: string | null;
  deviceName: string | null;
  staffName: string | null;
  clientTs: string | null;
  projectedAt: string | null;
  reviewedAt: string | null;
}

/**
 * The server prefixes a reason with this while a mutation is still queued behind something that
 * has not arrived. It is not a verdict, and the screen must not read it as one.
 */
const STILL_WAITING = 'waiting: ';

const isWaiting = (row: { status?: unknown; reason?: unknown }) =>
  row.status === 'pending' || (typeof row.reason === 'string' && row.reason.startsWith(STILL_WAITING));

/**
 * What the register was trying to do, in the infinitive — because none of it happened.
 *
 * Read out of the shared registry rather than through `enumLabel`, which falls back to `humanize()`
 * and would print `Order.lines.add` at a manager for a mutation type nobody has registered yet.
 * A sentence that says nothing is better than one that says our vocabulary out loud.
 */
function describeAction(type: unknown): string {
  const known = (ENUMS.offlineAction as Record<string, { label: string }>)[String(type)];
  return known?.label ?? 'Something a register sent';
}

/**
 * Not every refusal is a sentence. A body that failed VALIDATION arrives as the joined Zod report —
 * a schema path, a type name, and the literal contents of an enum:
 *
 *     Tenders.0.type: Invalid option: expected one of "cashUSD"|"cashHNL"|"card"|"venmo"|…
 *     Lines.0.qty: Invalid input: expected number, received undefined.
 *
 * All three of those were read aloud on this screen on 2026-07-28, and on the dashboard card headed
 * "38 things a register did were not carried out" that leads to it. That is our field names and our
 * stored enum values, on the one screen a manager opens while a guest is disputing a bill — and
 * there is nothing whatever he can do with any of it. What he needs is that the register sent something
 * this office could not read and that none of it reached a bill. The raw text is not lost: it stays
 * on the record, in the audit trail, for whoever fixes the register.
 *
 * Detected the way `api/errors.ts` already detects it: a leading `path:` whose head has no spaces
 * is a schema location, never prose. A refusal written by a domain — "that bill has already been
 * paid" — has no such prefix and passes through untouched.
 */
function isSchemaComplaint(said: string): boolean {
  const idx = said.indexOf(':');
  if (idx <= 0) return false;
  return /^[A-Za-z][\w.]*$/.test(said.slice(0, idx));
}

/** The reason, with the server's queue marker taken off the front. */
function describeReason(row: Record<string, unknown>): string {
  const reason = typeof row.reason === 'string' ? row.reason : '';
  if (!reason) {
    return isWaiting(row)
      ? 'It is queued behind something that has not reached the office yet.'
      : 'No reason was recorded.';
  }
  const said = reason.startsWith(STILL_WAITING) ? reason.slice(STILL_WAITING.length) : reason;
  if (isSchemaComplaint(said)) {
    return 'The register sent this in a form the office could not read, so it was not carried out. None of it is on a bill or in the takings.';
  }
  // The reasons are written as sentence fragments by the domain that refused ("that bill has
  // already been paid"), so the capital and the full stop are put on here rather than in twelve
  // separate handlers.
  return `${said.charAt(0).toUpperCase()}${said.slice(1)}${/[.!?]$/.test(said) ? '' : '.'}`;
}

function OutcomeCell({ row }: { row: Record<string, unknown> }) {
  const waiting = isWaiting(row);
  return (
    <Stack gap={4}>
      <Badge color={waiting ? 'yellow' : 'red'} variant="light" size="sm">
        {waiting ? 'Still trying' : 'Did not happen'}
      </Badge>
      <Text size="sm">{describeReason(row)}</Text>
    </Stack>
  );
}

/** When it was RUNG, which is the question — not when the message finally got here. */
function WhenCell({ row }: { row: Record<string, unknown> }) {
  const at = typeof row.clientTs === 'string' ? row.clientTs : null;
  if (!at) {
    return (
      <Text size="sm" c="dimmed">
        The register did not say
      </Text>
    );
  }
  return (
    <Stack gap={0}>
      <Text size="sm">{formatDateTime(at)}</Text>
      <Text size="xs" c="dimmed">
        {formatRelative(at)}
      </Text>
    </Stack>
  );
}

function AcknowledgeButton({ row, onClick }: { row: Record<string, unknown>; onClick: () => void }) {
  // Nothing to acknowledge on a row that may still apply by itself.
  if (isWaiting(row)) {
    return (
      <Text size="xs" c="dimmed">
        Still trying
      </Text>
    );
  }
  return (
    <Button size="compact-xs" variant="light" onClick={onClick}>
      I have dealt with this
    </Button>
  );
}

/**
 * The dialog is where the honesty about what this button does has to live, because the button
 * itself has room for five words and a manager who reads it as "approve it" has been misled by
 * this screen rather than by anything the platform did.
 */
function AcknowledgeModal({ row, onClose }: { row: Record<string, unknown> | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');

  const acknowledge = useMutation({
    mutationFn: (id: string) =>
      rawApi('POST', `${REJECTED_MUTATIONS_PATH}/${id}/reviewed`, { note: note.trim() || null }),
    onSuccess: () => {
      // By prefix, so the banner on the dashboard and the table here cannot disagree about how
      // many are outstanding — they are two readers of one endpoint.
      void queryClient.invalidateQueries({ queryKey: ['list', REJECTED_MUTATIONS_PATH] });
      notifyDone('Taken off the list', 'It is still recorded, and it still did not happen.');
      onClose();
    },
    onError: (err) => notifyError(err, 'mark this as dealt with'),
  });

  return (
    <Modal
      opened={row !== null}
      onClose={onClose}
      title={row ? describeAction(row.type) : ''}
      size="lg"
      closeOnClickOutside={false}
    >
      {row && (
        <Stack gap="sm">
          <Text size="sm">{describeReason(row)}</Text>
          <Alert color="yellow" title="This does not make it happen">
            <Text size="sm">
              It was refused and it stays refused. All this does is take it off the list and record that you
              saw it.
            </Text>
            <Text size="sm" mt="xs">
              If it should have stood, it has to be done again at the register by somebody allowed to do it —
              that way it goes on the bill and into the record properly.
            </Text>
          </Alert>
          <Textarea
            label="Anything worth remembering"
            description="Kept with the record of what you decided. Leave it blank if there is nothing to say."
            placeholder="Re-rang the comp on Wednesday morning"
            autosize
            minRows={2}
            maxLength={500}
            value={note}
            onChange={(event) => setNote(event.currentTarget.value)}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="default" onClick={onClose} disabled={acknowledge.isPending}>
              Cancel
            </Button>
            <Button loading={acknowledge.isPending} onClick={() => acknowledge.mutate(String(row.id))}>
              Take it off the list
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

/**
 * The list itself. Mounted under the device table rather than on a route of its own — see the
 * module header.
 */
export function RejectedActions() {
  const [dealingWith, setDealingWith] = useState<Record<string, unknown> | null>(null);

  /*
   * ABSENT WHEN THERE IS NOTHING IN IT. This sat under the Devices table as a second table whose
   * title only makes sense to somebody who already knows what an offline register queues — and on a
   * healthy bar it is empty every day, which made the Devices page read as broken ("a weird second
   * table that makes no sense" is the owner's own description of it). A table that exists to surface
   * exceptions has no claim on the screen while there are none; the day a register's offline night
   * includes something the office refused, it appears, and appearing IS the signal.
   */
  const any = useQuery({
    queryKey: listKey(REJECTED_MUTATIONS_PATH, UNREVIEWED),
    queryFn: () =>
      rawApi<{ mutations: RejectedAction[] }>('GET', `${REJECTED_MUTATIONS_PATH}?unreviewed=true`),
  });

  if ((any.data?.mutations ?? []).length === 0) {
    return null;
  }

  return (
    <>
      <CrudSection
        title="What a register sent that did not go through"
        description="A register with no internet keeps serving and sends the night up when it gets a signal. These are the parts this office would not carry out — none of them happened, and none of them is on a bill. If one of them should have stood, do it again at the register."
        listPath={REJECTED_MUTATIONS_PATH}
        listQuery={UNREVIEWED}
        extractList={(data) => (data as { mutations?: Record<string, unknown>[] })?.mutations ?? []}
        rowLabel={(row) => describeAction(row.type)}
        emptyMessage="Everything the registers have sent has gone through."
        columns={[
          { accessor: 'clientTs', title: 'When', render: (row) => <WhenCell row={row} />, noWrap: true },
          { accessor: 'type', title: 'What it was doing', render: (row) => describeAction(row.type) },
          {
            accessor: 'staffName',
            title: 'Who',
            render: (row) => (typeof row.staffName === 'string' ? row.staffName : '—'),
          },
          {
            accessor: 'deviceName',
            title: 'Which register',
            render: (row) => (typeof row.deviceName === 'string' ? row.deviceName : '—'),
          },
          { accessor: 'reason', title: 'Why it did not happen', render: (row) => <OutcomeCell row={row} /> },
        ]}
        rowActions={(row) => <AcknowledgeButton row={row} onClick={() => setDealingWith(row)} />}
      />
      <AcknowledgeModal row={dealingWith} onClose={() => setDealingWith(null)} />
    </>
  );
}

/**
 * The dashboard's line about it.
 *
 * Silent when the read fails, exactly as `RangeWarnings` is and for the same reason: a banner is an
 * extra on a screen that is about something else, and an error box for a list the reader cannot do
 * anything about from here is noise. The device screen reports the failure properly.
 */
export function RejectedActionsAlert() {
  const query = useQuery({
    queryKey: listKey(REJECTED_MUTATIONS_PATH, UNREVIEWED),
    queryFn: () =>
      rawApi<{ mutations: RejectedAction[] }>('GET', `${REJECTED_MUTATIONS_PATH}?unreviewed=true`),
  });

  const rows = query.data?.mutations ?? [];
  const refused = rows.filter((row) => !isWaiting(row));
  if (refused.length === 0) return null;

  return (
    <Alert
      color="red"
      title={
        refused.length === 1
          ? 'Something a register did was not carried out'
          : `${refused.length} things a register did were not carried out`
      }
      mb="md"
    >
      <Stack gap={4}>
        <Text size="sm">
          A register sent these up after being offline and this office would not perform them. None of them is
          on a bill or in the takings.
        </Text>
        <Group mt={4}>
          <Button component={Link} to="/devices" size="xs" variant="white" color="red">
            See what they were
          </Button>
        </Group>
      </Stack>
    </Alert>
  );
}
